import { useCallback } from 'react';
import { useSnackbar } from 'notistack';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { getDocumentName } from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';
import { buildPath } from '@e-socna/utils';
import type { Id } from '@e-socna/data-layer';

import {
  PATH_FOLDER_GENERAL,
  PATH_FOLDERS_DEFAULT,
} from '../../../app/constants/paths.constant';
import { userFriendlyErrors } from '../../../utils/back-errors.util';
import { useViewSnackbarAction } from '../../../hooks/useViewSnackbarAction.hook';
import { FolderPage_OneFolder_QueryDocument } from '../../../pages/Folder/hooks/useFindOneFolderQuery.hook';
import type { BackError } from '../../../utils/back-errors.util';
import type { WizardSubmitHandlerParams } from '../../../components/Wizard/types/submit-handler.type';

import { memoForm2ApiMapper } from '../../MemoBeforeSubmit/mappers/memo.form2api.mapper';
import { FormVariant } from '../../enums/variant.enum';

import { folderForm2ApiMapper } from '../mappers/folder.form2api.mapper';
import type { FolderForm_Folder } from '../fragments/folder.fragment';
import type { FolderFormTab } from '../enums/tab.enum';

import { useInitFolderMutation } from './useInitFolderMutation.hook';
import { useUpdateFolderMutation } from './useUpdateFolderMutation.hook';
import type { FolderForm_Folder_Init_Result } from './useInitFolderMutation.hook';
import type { FolderForm_Folder_Update_Result } from './useUpdateFolderMutation.hook';

export type FolderMutationResult =
  FolderForm_Folder_Init_Result | FolderForm_Folder_Update_Result;

interface Params {
  readonly folderId?: Id;
  readonly formVariant?: FormVariant;
  readonly handleSuccess?: (result: FolderMutationResult) => void;
}

export const useFolderSubmit = ({
  folderId,
  formVariant,
  handleSuccess,
}: Readonly<Params>) => {
  const { enqueueSnackbar } = useSnackbar();
  const { buildViewSnackbarAction } = useViewSnackbarAction();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: initMutation } = useInitFolderMutation();
  const { mutate: updateMutation } = useUpdateFolderMutation();

  const handleSubmit = useCallback(
    ({
      values,
      memos,
      formikHelpers: { setSubmitting },
      setWizardError,
    }: Readonly<
      WizardSubmitHandlerParams<FolderForm_Folder, FolderFormTab>
    >) => {
      setWizardError(undefined);

      const onSuccess = (result: FolderMutationResult) => {
        if (formVariant === FormVariant.Creation) {
          const { _id } = (result as FolderForm_Folder_Init_Result).initFolder;

          enqueueSnackbar('Votre nouveau dossier a bien été créé.', {
            action: buildViewSnackbarAction(
              buildPath(PATH_FOLDER_GENERAL, { folderId: _id }),
            ),
          });
        } else {
          enqueueSnackbar('Votre dossier a bien été mis à jour.');
          void queryClient.invalidateQueries({
            queryKey: [
              getDocumentName(FolderPage_OneFolder_QueryDocument),
              { id: folderId },
            ],
          });
        }

        if (handleSuccess) {
          handleSuccess(result);
        } else if (!formVariant || formVariant === FormVariant.Creation) {
          void navigate(PATH_FOLDERS_DEFAULT);
        }

        setSubmitting(false);
      };

      const onError = (error: unknown) => {
        setWizardError({
          messages: userFriendlyErrors(error as BackError),
        });

        setSubmitting(false);
      };

      if (formVariant === FormVariant.Creation) {
        initMutation(
          {
            folder: folderForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      } else {
        updateMutation(
          {
            id: folderId ?? EMPTY_ID,
            update: folderForm2ApiMapper(values),
            versionData: memoForm2ApiMapper(memos),
          },
          {
            onError,
            onSuccess,
          },
        );
      }

      return undefined;
    },
    [
      folderId,
      buildViewSnackbarAction,
      enqueueSnackbar,
      formVariant,
      handleSuccess,
      navigate,
      queryClient,
      initMutation,
      updateMutation,
    ],
  );

  return { handleSubmit };
};
